import { defineStore } from 'pinia'
import browser from 'webextension-polyfill'
import { Locale } from 'vue-i18n'

import {
  FALLBACK_LOCALE as fallbackLocale,
  IDLE_TIMEOUT,
  DEFAULT_IDLE_TIMEOUT_IN_MINUTES,
  DEFAULT_NATIVE_TOKEN_SYMBOL,
  MAX_IDLE_TIMEOUT_IN_MINUTES,
} from '@/shared/config'
import {
  getCurrencies,
  Currency,
  CurrencySymbol,
  CURRENT_CURRENCY,
  CURRENCY_FALLBACK,
} from '@/popup/lib/currencies'
import { setNumberFormats } from '@/popup/lib/i18n'

type Token = {
  symbol: string
}

export type PreferencesState = {
  token: Token
  currencies: Currency
  currentCurrency?: CurrencySymbol
  locale: Locale
  idleTimeout: number
}

export async function setIdleTimeoutInMinutes(
  idleTimeoutInMinutes?: number | string
) {
  // Disallow invalid values during write
  idleTimeoutInMinutes =
    typeof idleTimeoutInMinutes === 'string'
      ? Number.parseInt(idleTimeoutInMinutes)
      : typeof idleTimeoutInMinutes === 'number'
      ? idleTimeoutInMinutes
      : undefined
  idleTimeoutInMinutes =
    typeof idleTimeoutInMinutes === 'number'
      ? Math.min(MAX_IDLE_TIMEOUT_IN_MINUTES, Math.max(idleTimeoutInMinutes, 1))
      : DEFAULT_IDLE_TIMEOUT_IN_MINUTES
  await browser.storage.local.set({
    [IDLE_TIMEOUT]: idleTimeoutInMinutes,
  })
  return idleTimeoutInMinutes
}

export async function getIdleTimeoutInMinutes(): Promise<number> {
  const store = await browser.storage.local.get(IDLE_TIMEOUT)
  let { [IDLE_TIMEOUT]: idleTimeoutInMinutes } = store || {}
  if (idleTimeoutInMinutes) {
    // Disallow invalid values during read
    idleTimeoutInMinutes =
      typeof idleTimeoutInMinutes === 'string'
        ? Number.parseInt(idleTimeoutInMinutes)
        : typeof idleTimeoutInMinutes === 'number'
        ? idleTimeoutInMinutes
        : undefined
    idleTimeoutInMinutes =
      typeof idleTimeoutInMinutes === 'number'
        ? Math.min(
            MAX_IDLE_TIMEOUT_IN_MINUTES,
            Math.max(idleTimeoutInMinutes, 1)
          )
        : undefined
    return idleTimeoutInMinutes ?? DEFAULT_IDLE_TIMEOUT_IN_MINUTES
  }
  return setIdleTimeoutInMinutes(DEFAULT_IDLE_TIMEOUT_IN_MINUTES)
}

export const usePreferencesStore = defineStore('preferences', {
  state: () =>
    ({
      token: {
        symbol: DEFAULT_NATIVE_TOKEN_SYMBOL,
      },
      currencies: {},
      currentCurrency: undefined,
      locale: fallbackLocale,
      idleTimeout: DEFAULT_IDLE_TIMEOUT_IN_MINUTES,
    }) as PreferencesState,
  actions: {
    async init(locale: Locale, nativeTokenSymbol: string) {
      this.locale = locale
      this.token = { symbol: nativeTokenSymbol }
      this.currencies = await getCurrencies(nativeTokenSymbol)
      this.currentCurrency = await this.getCurrentCurrency()
      this.idleTimeout = await this.getIdleTimeoutInMinutes()
    },
    setLocale(locale: Locale) {
      this.locale = locale
    },
    async setCurrentCurrency(currencySymbol: CurrencySymbol) {
      setNumberFormats(this.locale, currencySymbol)
      await browser.storage.local.set({
        [CURRENT_CURRENCY]: currencySymbol,
      })
      this.currentCurrency = currencySymbol
    },
    async getCurrentCurrency(): Promise<CurrencySymbol> {
      let currency = CURRENCY_FALLBACK

      const localStorageCurrency =
        await browser.storage.local.get(CURRENT_CURRENCY)

      if (localStorageCurrency && localStorageCurrency[CURRENT_CURRENCY]) {
        currency = localStorageCurrency[CURRENT_CURRENCY]
      } else {
        this.setCurrentCurrency(currency)
      }

      return currency
    },

    async setIdleTimeoutInMinutes(idleTimeoutInMinutes?: number | string) {
      this.idleTimeout = await setIdleTimeoutInMinutes(idleTimeoutInMinutes)
    },

    async getIdleTimeoutInMinutes(): Promise<number> {
      return (this.idleTimeout = await getIdleTimeoutInMinutes())
    },
  },
})
